import { Box, FormHelperText, FormLabel, Slider, Typography } from "@mui/joy";
import { useMemo } from "react";
import { useFormContext, useController } from "react-hook-form";

import { useGradesQuery } from "../../hooks/grades";
import { getAllGradeValuesForSystem } from "../../utils/grades";
import FormGroup from "./FormGroup";
import { GradeSystemDropdown } from "./GradePicker";

type GradeChangeEvent = {
  minGradeValue: string | null | undefined;
  maxGradeValue: string | null | undefined;
  gradeSystem: string;
};

type Props = {
  gradeSystem: string;
  gradeSystemChoices: string[];
  minGradeValue: string | null | undefined;
  maxGradeValue: string | null | undefined;
  onChange: (change: GradeChangeEvent) => void;
};

export default function GradeRangeSlider({
  gradeSystem,
  minGradeValue,
  maxGradeValue,
  gradeSystemChoices,
  onChange,
}: Props) {
  const { data: gradesData } = useGradesQuery();

  const gradeValues = useMemo(() => {
    if (!gradesData || !gradeSystem) {
      return [];
    }
    return [...getAllGradeValuesForSystem(gradeSystem)];
  }, [gradesData, gradeSystem]);

  const onChangeGradeSystem = (newGradeSystem: string) => {
    onChange({
      minGradeValue: null,
      maxGradeValue: null,
      gradeSystem: newGradeSystem,
    });
  };

  // A null/undefined bound means "no bound", which includes routes with an unknown grade. An
  // explicit bound, even the lowest or highest grade, excludes them. The track has a "no bound"
  // position at each end: [null, ...gradeValues, null]. Position i (1..n) is gradeValues[i - 1].
  const gradeCount = gradeValues.length;
  const topPosition = gradeCount + 1;
  const positionToGrade = (position: number) =>
    position <= 0 || position >= topPosition ? null : gradeValues[position - 1];
  const gradeToPosition = (grade: string | null | undefined, fallback: number) => {
    const index = grade == null ? -1 : gradeValues.indexOf(grade);
    return index === -1 ? fallback : index + 1;
  };

  const minPosition = gradeToPosition(minGradeValue, 0);
  const maxPosition = gradeToPosition(maxGradeValue, topPosition);

  return (
    <Box>
      <Typography
        sx={{
          textAlign: "center",
          fontSize: 24,
          fontWeight: "bold",
        }}
      >
        {positionToGrade(minPosition) ?? "Any"} - {positionToGrade(maxPosition) ?? "Any"}
      </Typography>
      <Slider
        value={[minPosition, maxPosition]}
        min={0}
        max={topPosition}
        onChange={(_event, value) => {
          if (!(value instanceof Array) || gradeCount === 0) {
            return;
          }
          // The min handle can't be "no bound" at the top, nor the max handle at the bottom, so
          // clamp each to the nearest real grade.
          const [newMin, newMax] = value;
          onChange({
            minGradeValue: positionToGrade(Math.min(newMin, gradeCount)),
            maxGradeValue: positionToGrade(Math.max(newMax, 1)),
            gradeSystem,
          });
        }}
      />
      <Box
        sx={{
          justifyContent: "center",
          display: "flex",
        }}
      >
        <GradeSystemDropdown
          gradeSystem={gradeSystem}
          choices={gradeSystemChoices}
          onChange={onChangeGradeSystem}
        />
      </Box>
    </Box>
  );
}

export const GradeRangeInput = ({
  names,
  label,
  gradeSystemChoices,
}: {
  label: string;
  names?: {
    minGradeValue?: string;
    maxGradeValue?: string;
    gradeSystem?: string;
  };
  gradeSystemChoices: string[];
}) => {
  const { control } = useFormContext();
  const { field: minField, fieldState: minFieldState } = useController({
    name: names?.minGradeValue || "minGradeValue",
    control,
  });
  const { field: maxField, fieldState: maxFieldState } = useController({
    name: names?.maxGradeValue || "maxGradeValue",
    control,
  });
  const { field: systemField, fieldState: systemFieldState } = useController({
    name: names?.gradeSystem || "gradeSystem",
    control,
  });
  const hasError = !!(minFieldState.error || maxFieldState.error || systemFieldState.error);
  return (
    <FormGroup>
      <FormLabel>{label}</FormLabel>
      <GradeRangeSlider
        minGradeValue={minField.value}
        maxGradeValue={maxField.value}
        gradeSystem={systemField.value}
        gradeSystemChoices={gradeSystemChoices}
        onChange={(change) => {
          minField.onChange(change.minGradeValue);
          maxField.onChange(change.maxGradeValue);
          systemField.onChange(change.gradeSystem);
        }}
      />
      {hasError ? (
        <FormHelperText>
          {minFieldState.error?.message}
          {maxFieldState.error?.message}
          {systemFieldState.error?.message}
        </FormHelperText>
      ) : null}
    </FormGroup>
  );
};
